import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getLeagueStatus, previewStatus } from "./leagueStatus";
import { cashMetaParts, previewSecondLine } from "./leagueFormat";
import { ChevronRightIcon, AvatarPersonIcon } from "./icons";
import useMinuteTick from "./useMinuteTick";
import { PANEL_BLEED } from "./PublicLeagueRow";
import { competitionDestination } from "../../../utils/leaguePath";

// A Cash Game preview row, for the Cash Games View All screen.
//
// Client: "Bring the Cash Games View All pages over from Public Leagues with the
// same Starting Soon, Live Now, and Completed tabs. we are using are exact card
// system for these screens. Keep the same card layout, spacing, typography,
// timing placement, clickable row behavior, and chevron."
//
// So this is PublicLeagueRow's skeleton, unchanged: identity line with the
// status right-aligned against a constant edge, title with the chevron pinned
// right and vertically centred against the whole row, second line, metadata.
// Same four type steps (#0F172A / #4B5563 / #6B7280 / #9CA3AF), same hover, same
// full-row link. Only the two things the client listed differ:
//
//   1. THE IDENTITY LINE. A cash game is always hosted by QUSHi, so naming the
//      organiser on every row would print "QUSHi" three tabs deep and tell the
//      reader nothing. The client replaces it per phase:
//        Starting Soon  ->  HOSTED BY [QUSHi avatar] QUSHi
//        Live Now       ->  LEADER    [avatar] username
//        Completed      ->  WINNER    [avatar] username
//      The leader/winner is whoever is top of the game's board, which travels
//      with the list as `leading_player` (GameController::leadingPlayers).
//
//   2. THE METADATA. Experience level out, the dynamic prize pool in, and
//      "League" becomes "Game" — see cashMetaParts.
//
// A separate component rather than a `variant` prop on PublicLeagueRow: that row
// is THE league preview for the whole site and its comment says so. Threading a
// cash-game identity mode through it would put a cash-game rule inside the
// component every other screen renders, which is how the one design starts
// drifting into two.

// The eyebrow label and whose face goes beside it, for the phase this row is in.
// `phase` is the tab's own key, so the row can never disagree with the list it
// was filed under — the tab decided, and this reads that decision rather than
// re-deriving it from the dates.
const identityFor = (phase, game) => {
  const player = game?.leading_player;
  if (phase === "live" && player?.user_name) {
    return { label: "Leader", handle: player.user_name, avatar: player.image };
  }
  if (phase === "done" && player?.user_name) {
    return { label: "Winner", handle: player.user_name, avatar: player.image };
  }
  // Starting Soon, and the honest fallback for a live or finished game that has
  // no qualifying player yet: nobody is leading a game nobody has traded in, and
  // inventing a name would be worse than naming the house that is running it.
  return { label: "Hosted by", handle: "QUSHi", avatar: game?.user?.image || "" };
};

const IdentityAvatar = ({ src }) => {
  const [broken, setBroken] = useState(false);
  const show = !!src && !broken;
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F3F4F6]">
      {show ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <AvatarPersonIcon className="h-3 w-3" />
      )}
    </span>
  );
};

const CashGameRow = ({ game, phase = "soon", bleed = PANEL_BLEED.wide }) => {
  const status = getLeagueStatus(game);
  const chip = previewStatus(status, game);
  const second = previewSecondLine(game, status);
  const meta = cashMetaParts(game);
  const identity = identityFor(phase, game);

  useMinuteTick(!!status?.countdownValue);

  return (
    <Link
      /* Client: "All of league play should carry the same state logic,
         including cash games." This row sent EVERY state to the landing page.
         It is the fallback rendering in Cash Games > View All — all three tabs
         currently render cards instead, which is why it never appeared in
         testing — but an unreachable inconsistency is still one waiting to be
         reached. */
      to={competitionDestination(game, status)}
      className={`group block py-4 transition-colors hover:bg-[#F8FAFA] ${bleed}`}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              {/* The eyebrow is the only addition to the identity line: HOSTED
                  BY / LEADER / WINNER, in small caps ahead of the avatar, exactly
                  as the client wrote it. */}
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-tertiary">
                {identity.label}
              </span>
              <IdentityAvatar src={identity.avatar} />
              <span className="truncate text-sm text-secondary">{identity.handle}</span>
            </span>
            {/* Starts in / Ends in / Ended <date>, right-aligned in its own
                column and in the Public League colours — the client asked for
                both explicitly: "Keep Starts in, Ends in, and Ended date in the
                same top right position and colors used on Public League cards."
                previewStatus is where those live, so this reads it rather than
                restating the colours. */}
            {chip?.text && (
              <span
                className={`shrink-0 text-right text-sm font-semibold ${
                  chip.pill ? "rounded-full px-2 py-0.5 font-medium" : ""
                } ${chip.cls}`}
              >
                {chip.text}
              </span>
            )}
          </div>

          <h3 className="mt-1.5 font-montserrat font-bold text-base md:text-lg text-[#0F172A] leading-snug">
            {game.title}
          </h3>

          {second && (
            <p
              className="mt-1 overflow-hidden text-sm text-[#4B5563]"
              // Same inline clamp as PublicLeagueRow, and for the same reason:
              // this build's autoprefixer strips `display:-webkit-box`, so the
              // Tailwind line-clamp utility alone clamps nothing.
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {second.tagline || (
                <>
                  {second.label}{" "}
                  <span className={`font-semibold ${second.valueClass}`}>
                    {second.value}
                  </span>
                </>
              )}
            </p>
          )}

          {meta.primary && (
            <p className="mt-2 text-xs">
              <span className="text-[#6B7280]">{meta.primary}</span>
            </p>
          )}
        </div>

        {/* "Do not add a separate CTA to the View All cards. The entire card is
            tappable and uses the existing chevron system." — the Link above is
            the whole row, and this is that chevron. */}
        <ChevronRightIcon className="h-5 w-5 shrink-0 text-[#9CA3AF] transition-colors group-hover:text-[#0F4146]" />
      </div>
    </Link>
  );
};

export default CashGameRow;
